'use client';
import { Sys, MediaAsset, AspectRatio, LottieAnimationData } from 'libs/contentful/types';
import Image, { getImageProps } from 'next/image';
import { MediaContentTypes } from 'types/constants';
import { cn } from 'utils/styles';
import { createAspectRatioImageLoader } from 'utils/imageLoader';
import { Breakpoints } from 'types/constants';
import LottieAnimation from 'components/shared/LottieAnimation/LottieAnimation';
import { AnimationSettings } from 'types/sharedComponents';
import { isValidAnimation } from '@/components/shared/Media/mediaUtils';
import { preload } from 'react-dom';

export interface MediaProps {
  sys?: Sys;
  __typename?: string;
  className?: string;
  media?: MediaAsset;
  mobileMedia?: MediaAsset;
  loading?: 'lazy' | 'eager';
  isLcp?: boolean;
  sizes?: string;
  aspectRatio?: AspectRatio;
  mobileAspectRatio?: AspectRatio;
  // Lottie animation props
  desktopAnimation?: LottieAnimationData | null;
  mobileAnimation?: LottieAnimationData | null;
  animationSettings?: AnimationSettings;
  roundedImage?: boolean;
}

const getImageDimensions = (media: MediaAsset) =>
  media.fill ? { fill: media.fill } : { width: media.width, height: media.height };

const getMediaImageProps = (
  source: MediaAsset,
  dimensions: Pick<MediaAsset, 'fill' | 'width' | 'height'>,
  sizes?: string,
  aspectRatio?: AspectRatio,
) =>
  getImageProps({
    sizes,
    ...dimensions,
    src: source.url,
    alt: source.description ?? '',
    loader: createAspectRatioImageLoader(source, aspectRatio?.value),
  });

const MOBILE_MEDIA_QUERY = `(max-width: ${Breakpoints.Mobile}px)`;
const DESKTOP_MEDIA_QUERY = `(min-width: ${Breakpoints.Mobile + 1}px)`;

type ImageAttributes = ReturnType<typeof getImageProps>['props'];

// Must run while this Client Component renders: Next only carries ReactDOM resource hints
// into the server HTML from Client Components, so the Server Component heroes can't call it.
const preloadResponsiveSources = (desktop: ImageAttributes, mobile: ImageAttributes, sizes?: string) => {
  const options = { as: 'image', imageSizes: sizes, fetchPriority: 'high' } as const;

  // React keys image preloads on srcset + sizes only, so hints differing just by media
  // would collapse into the first; identical sources need one unscoped hint.
  if (mobile.srcSet === desktop.srcSet) {
    preload(desktop.src, { ...options, imageSrcSet: desktop.srcSet });
  } else {
    preload(mobile.src, { ...options, imageSrcSet: mobile.srcSet, media: MOBILE_MEDIA_QUERY });
    preload(desktop.src, { ...options, imageSrcSet: desktop.srcSet, media: DESKTOP_MEDIA_QUERY });
  }
};

const Media = ({
  className,
  media,
  mobileMedia,
  loading,
  isLcp = false,
  sizes,
  aspectRatio,
  mobileAspectRatio,
  desktopAnimation,
  mobileAnimation,
  animationSettings,
  roundedImage = true,
  ...rest
}: MediaProps) => {
  // Check if animations are present
  const hasDesktopAnimation = !!desktopAnimation && isValidAnimation(desktopAnimation);
  const hasMobileAnimation = !!mobileAnimation && isValidAnimation(mobileAnimation);
  const hasAnyAnimation = hasDesktopAnimation || hasMobileAnimation;

  // If animations are present, render Lottie instead of static images
  if (hasAnyAnimation) {
    return (
      <div className={className} {...rest}>
        {hasDesktopAnimation && (
          <div className="hidden sm:block">
            <LottieAnimation src={desktopAnimation} animationSettings={animationSettings} />
          </div>
        )}
        {hasMobileAnimation && (
          <div className="block sm:hidden">
            <LottieAnimation src={mobileAnimation} animationSettings={animationSettings} />
          </div>
        )}
      </div>
    );
  }

  if (!media?.url) {
    return null;
  }

  const fillOrDimensions = getImageDimensions(media);

  // For native img tags, exclude fill prop (only valid for Next.js Image)
  const imgDimensions = media.fill
    ? {
        width: media.width,
        height: media.height,
      }
    : fillOrDimensions;

  const desktopImage = getMediaImageProps(media, fillOrDimensions, sizes, aspectRatio).props;
  const mobileImage = getMediaImageProps(mobileMedia || media, fillOrDimensions, sizes, mobileAspectRatio).props;
  const isImage = media.contentType.includes(MediaContentTypes.Image);

  // <picture> candidates are invisible to React's automatic image preloading, which
  // already covers the single-source <Image> branch.
  if (isLcp && isImage && mobileMedia) {
    preloadResponsiveSources(desktopImage, mobileImage, sizes);
  }

  return isImage ? (
    mobileMedia ? (
      <picture data-testid="responsive-image">
        <source srcSet={mobileImage.srcSet} media={MOBILE_MEDIA_QUERY} sizes={sizes} />
        <source srcSet={desktopImage.srcSet} media={DESKTOP_MEDIA_QUERY} sizes={sizes} />
        <img
          {...rest}
          {...imgDimensions}
          src={desktopImage.src}
          style={desktopImage.style}
          alt={media.description ?? ''}
          className={cn(`object-cover`, roundedImage && 'rounded-lg', className)}
          loading={isLcp ? 'eager' : (loading ?? 'lazy')}
          fetchPriority={isLcp ? 'high' : undefined}
        />
      </picture>
    ) : (
      <Image
        alt={media.description ?? ''}
        className={cn(`object-cover`, roundedImage && 'rounded-lg', className)}
        src={media.url}
        loading={isLcp ? 'eager' : (loading ?? 'lazy')}
        fetchPriority={isLcp ? 'high' : undefined}
        sizes={sizes}
        loader={createAspectRatioImageLoader(media, aspectRatio?.value)}
        {...fillOrDimensions}
        {...rest}
      />
    )
  ) : null;
};

export default Media;
